// src/components/Sidebar.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Icons = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  stores: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  coupons: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  ),
  products: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
  analytics: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
      <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

const NAV = [
  { key: 'dashboard', label: 'Dashboard', path: '/' },
  { key: 'stores',    label: 'My Stores',  path: '/stores' },
  { key: 'coupons',   label: 'Coupons',    path: '/coupons' },
  { key: 'products',  label: 'Products',   path: '/products' },
  { key: 'analytics', label: 'Analytics',  path: '/analytics' },
  { key: 'profile',   label: 'Profile',    path: '/profile' },
];

const PRIMARY_NAV = ['dashboard', 'products', 'coupons'];
const MORE_NAV    = ['stores', 'analytics', 'profile'];

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1" fill="currentColor"/><circle cx="12" cy="12" r="1" fill="currentColor"/><circle cx="12" cy="19" r="1" fill="currentColor"/>
  </svg>
);

export default function Sidebar({ active }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const initials = user?.storeOwner?.name?.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
  const [moreOpen, setMoreOpen] = useState(false);
  const isMoreActive = MORE_NAV.includes(active);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <h2>Shoppr</h2>
          <div className="sidebar-tagline">Owner Portal</div>
        </div>

        <nav className="sidebar-nav">
          {NAV.map(item => (
            <button
              key={item.key}
              className={`nav-item ${active === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              {Icons[item.key]}
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.storeOwner?.name}</div>
              <div className="sidebar-user-role">Store Owner</div>
            </div>
          </div>
          <button className="nav-item" onClick={logout} style={{ marginTop: 4 }}>
            {Icons.logout}
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav className="bottom-nav">
        {NAV.filter(i => PRIMARY_NAV.includes(i.key)).map(item => (
          <button
            key={item.key}
            className={`bottom-nav-item ${active === item.key ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
          >
            <span className="bottom-nav-icon">{Icons[item.key]}</span>
            <span className="bottom-nav-label">{item.label}</span>
          </button>
        ))}
        <button
          className={`bottom-nav-item ${isMoreActive ? 'active' : ''}`}
          onClick={() => setMoreOpen(true)}
        >
          <span className="bottom-nav-icon"><MoreIcon /></span>
          <span className="bottom-nav-label">More</span>
        </button>
      </nav>

      {/* More sheet */}
      {moreOpen && (
        <>
          <div className="more-sheet-backdrop" onClick={() => setMoreOpen(false)} />
          <div className="more-sheet">
            <div className="more-sheet-handle" />
            {NAV.filter(i => MORE_NAV.includes(i.key)).map(item => (
              <button key={item.key} className={`more-sheet-item ${active === item.key ? 'active' : ''}`}
                onClick={() => { navigate(item.path); setMoreOpen(false); }}>
                <span className="more-sheet-icon">{Icons[item.key]}</span>
                <span>{item.label}</span>
              </button>
            ))}
            <div className="more-sheet-divider" />
            <button className="more-sheet-item danger" onClick={() => { logout(); setMoreOpen(false); }}>
              <span className="more-sheet-icon">{Icons.logout}</span>
              <span>Sign Out</span>
            </button>
          </div>
        </>
      )}
    </>
  );
}
